import React from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import { PeriodDropdown, PeriodOption } from './PeriodDropdown';
import { DateRangePicker } from './DateRangePicker';
import type { CustomDateRange } from '../types';

interface StatCardProps {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  theme: ThemeColors;
  isMobile: boolean;
  period: PeriodOption;
  onPeriodChange: (value: PeriodOption) => void;
  customRange?: CustomDateRange;
  onCustomRangeChange?: (range: CustomDateRange | undefined) => void;
  onActionClick?: () => void;
  actionLabel?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  change,
  positive,
  theme,
  isMobile,
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
  onActionClick,
  actionLabel,
}) => {
  // Check if the change is 0% (e.g., "+0.00%", "0%", "+0%")
  const isNoChange = /^[+-]?0(\.0+)?%/.test(change);

  const styles = {
    card: {
      background: theme.card,
      borderRadius: '8px',
      padding: '16px',
      position: 'relative' as const,
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    },
    statLabel: {
      fontSize: '13px',
      color: theme.textSecondary,
      marginBottom: '6px',
      fontWeight: 500,
    },
    statValue: {
      fontSize: isMobile ? '28px' : '32px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '6px',
      lineHeight: 1.1,
    },
    statChange: {
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: 500,
      color: isNoChange ? '#6b7280' : positive ? '#22c55e' : '#ef4444',
    },
  };

  // Extract the period label from the change string (e.g., "from yesterday")
  const periodLabel = change.replace(/^[+-]?\d+(\.\d+)?%\s*/, '');

  return (
    <div style={styles.card}>
      <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '4px', alignItems: 'center' }}>
        <PeriodDropdown
          value={period}
          onChange={onPeriodChange}
          theme={theme}
          style={{ position: 'relative', top: 'auto', right: 'auto' }}
        />
        {onCustomRangeChange && (
          <DateRangePicker
            value={customRange}
            onChange={onCustomRangeChange}
            theme={theme}
            style={{ position: 'relative', top: 'auto', right: 'auto' }}
            isActive={period === 'custom'}
          />
        )}
      </div>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statChange}>
        {isNoChange ? (
          <>No Change {periodLabel}</>
        ) : (
          <>
            <span>{positive ? '↑' : '↓'}</span>
            {change}
          </>
        )}
      </div>
      {onActionClick && (
        <button
          onClick={onActionClick}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#3b82f6',
            fontSize: '11px',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '2px 0',
            marginTop: '4px',
          }}
        >
          {actionLabel || 'View'} →
        </button>
      )}
    </div>
  );
};
